import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { IProject } from '@featit/shared';
import { useStores } from '@/hooks/useStores';
import { isAllWorkAreasScope } from '@/features/work-areas/workAreaOverviewUrls';
import { addressFollowsAppScope } from '@/shell/appScope';
import { isShellSpare } from '@/shell/shellSpare';
import { appScopeFollowingFrom } from '@/shell/useAppScope';

/**
 * The waiting decisions for the scope the reader is in (I-0480, I-0560 AC-5), read wherever the app
 * is drawn. It was read by the pinned sidebar, which T-0559 cut B draws only from `lg` up: below
 * that nothing read it, and answering a decision opened on its own (a link, a push) found no next
 * waiting one and went back to the list (IU-8's *next-waiting*, gate 628). The scope is the
 * sidebar's own: the address's work area, else the namespace or the selection.
 *
 * Read in an observer (`Layout`), so a change of the scope reads again.
 *
 * I-1578 (RD-40): never on the hidden spare web view. It shows no scope, and its window's selection
 * is not the app's: at a launch on All it read the first work area between two reads of All, and on
 * a resume after a pick the old work area three times. Routed to a tab, it reads for that tab.
 */
export function useDecisionsWaitingRead(addressWorkArea: IProject | null | undefined): void {
  const { projectStore, decisionsStore } = useStores();
  const location = useLocation();
  const spare = isShellSpare(location.pathname);
  const overviewMode = addressFollowsAppScope(
    location.pathname,
    projectStore.appScope,
    projectStore.projects,
    appScopeFollowingFrom(),
  )
    ? projectStore.allWorkAreas
    : isAllWorkAreasScope(location.pathname, projectStore.allWorkAreas);
  const namespaceScopeId = projectStore.selectedNamespaceId;
  const workArea =
    addressWorkArea !== undefined
      ? addressWorkArea
      : namespaceScopeId
        ? null
        : projectStore.selectedProject;
  const selectedWorkAreaId = workArea?.id ?? null;
  useEffect(() => {
    if (spare) return;
    if (overviewMode) void decisionsStore.loadAllWorkAreas();
    else if (namespaceScopeId) void decisionsStore.loadNamespace(namespaceScopeId);
    else if (selectedWorkAreaId) void decisionsStore.load(selectedWorkAreaId);
  }, [spare, overviewMode, namespaceScopeId, selectedWorkAreaId, decisionsStore]);
}
